import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Upload, FileText, RefreshCw, Download, ClipboardPaste, Code2, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = ['PENDING', 'APPROVED', 'DECLINED', 'PAID OUT', 'incomplete application'];

// Full-row colour per status
const ROW_COLORS = {
  'APPROVED':              'bg-emerald-600/30 hover:bg-emerald-600/40',
  'DECLINED':              'bg-rose-600/30 hover:bg-rose-600/40',
  'PENDING':               'bg-amber-500/30 hover:bg-amber-500/40',
  'PAID OUT':              'bg-blue-600/30 hover:bg-blue-600/40',
  'incomplete application':'bg-slate-500/30 hover:bg-slate-500/40',
};

const STATUS_CHIP = {
  'APPROVED':              'bg-emerald-600 text-white',
  'DECLINED':              'bg-rose-600 text-white',
  'PENDING':               'bg-amber-500 text-black',
  'PAID OUT':              'bg-blue-600 text-white',
  'incomplete application':'bg-slate-500 text-white',
};

const REMINDER_CHIP = {
  'Need to vet':           'bg-amber-700/60 text-amber-100 border-amber-500',
  'Awaiting Agreement OTP':'bg-emerald-800/60 text-emerald-100 border-emerald-500',
  'Complete':              'bg-blue-800/60 text-blue-100 border-blue-500',
  'Declined':              'bg-rose-800/60 text-rose-100 border-rose-500',
  'Waiting Period':        'bg-slate-700 text-slate-100 border-slate-500',
  'Waiting Period Over':   'bg-orange-800/60 text-orange-100 border-orange-500',
};

const EMPTY = {
  month: '', date: new Date().toISOString().slice(0, 10),
  account_no: '', surname: '', first_name: '', id_number: '', cell_number: '',
  channel: 'ONLINE APPLICATION', own_reference: 'Own Ref', balance: 0,
  status: 'PENDING', comments: '', action: '',
};

export default function LoansTracker() {
  const [loans, setLoans] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  // Bulk paste
  const [showPaste, setShowPaste] = useState(false);
  const [showApi, setShowApi] = useState(false);
  const [apiConfig, setApiConfig] = useState(null);
  const [apiTab, setApiTab] = useState('html');

  const openApiDialog = async () => {
    setShowApi(true);
    if (apiConfig) return;
    try {
      const res = await axios.get(`${API_URL}/api/loans-tracker/public-config`, { headers });
      setApiConfig(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load API config');
      setShowApi(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Copy failed — please select and copy manually');
    }
  };
  const [pasteText, setPasteText] = useState('');
  const [pasteDate, setPasteDate] = useState(new Date().toISOString().slice(0, 10));

  const fileInputRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLoans = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      const res = await axios.get(`${API_URL}/api/loans-tracker`, { headers, params });
      setLoans(res.data.loans || []);
      setTotals(res.data.totals || {});
    } catch {
      toast.error('Failed to load loans');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterStatus]);

  useEffect(() => { const t = setTimeout(fetchLoans, 250); return () => clearTimeout(t); }, [fetchLoans]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (loan) => { setEditing(loan); setForm({ ...loan }); setShowForm(true); };

  const saveLoan = async () => {
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/loans-tracker/${editing.id}`, form, { headers });
        toast.success('Loan updated');
      } else {
        await axios.post(`${API_URL}/api/loans-tracker`, form, { headers });
        toast.success('Loan added');
      }
      setShowForm(false);
      fetchLoans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const deleteLoan = async (loan) => {
    if (!window.confirm(`Delete loan for ${loan.first_name} ${loan.surname}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/loans-tracker/${loan.id}`, { headers });
      toast.success('Deleted');
      fetchLoans();
    } catch {
      toast.error('Delete failed');
    }
  };

  // Inline status change
  const changeStatus = async (loan, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/loans-tracker/${loan.id}`, { ...loan, status: newStatus }, { headers });
      fetchLoans();
    } catch {
      toast.error('Update failed');
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/loans-tracker/upload-csv`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${res.data.inserted} loans (${res.data.skipped} skipped)`);
      fetchLoans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV import failed');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async () => {
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      const res = await axios.get(`${API_URL}/api/loans-tracker/export-csv`, {
        headers, params, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `loan_tracker_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Download failed');
    }
  };

  const handleBulkPaste = async () => {
    const text = pasteText.trim();
    if (!text) { toast.error('Paste some rows first'); return; }
    try {
      const res = await axios.post(
        `${API_URL}/api/loans-tracker/bulk-paste`,
        { text, default_date: pasteDate },
        { headers }
      );
      toast.success(`Imported ${res.data.inserted} loans (${res.data.skipped} skipped)`);
      setShowPaste(false);
      setPasteText('');
      fetchLoans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk paste failed');
    }
  };

  const totalBalance = loans.reduce((a, b) => a + (Number(b.balance) || 0), 0);

  return (
    <DashboardLayout>
      <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} data-testid="csv-file-input" />

      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <FileText className="h-7 w-7 text-blue-500" />
              2K2Day — Loan Tracker
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Capture loan applications, track statuses, upload CSVs and see auto-reminders.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="upload-csv-btn">
              <Upload className="h-4 w-4 mr-1.5" /> Upload CSV
            </Button>
            <Button variant="outline" onClick={() => setShowPaste(true)} data-testid="bulk-paste-btn">
              <ClipboardPaste className="h-4 w-4 mr-1.5" /> Paste from Excel
            </Button>
            <Button variant="outline" onClick={handleDownload} data-testid="download-csv-btn">
              <Download className="h-4 w-4 mr-1.5" /> Download CSV
            </Button>
            <Button variant="outline" onClick={openApiDialog} data-testid="public-api-btn">
              <Code2 className="h-4 w-4 mr-1.5" /> Public API / Embed
            </Button>
            <Button variant="outline" onClick={fetchLoans}>
              <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
            </Button>
            <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="add-loan-btn">
              <Plus className="h-4 w-4 mr-1.5" /> New Loan
            </Button>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {STATUS_OPTIONS.map(s => (
            <Card key={s} className="cursor-pointer" onClick={() => setFilterStatus(filterStatus === s ? 'all' : s)}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${STATUS_CHIP[s]}`}>{s}</span>
                  <span className="text-xl font-bold">{totals[s] || 0}</span>
                </div>
              </CardContent>
            </Card>
          ))}
          <Card>
            <CardContent className="p-3">
              <div className="text-[10px] text-muted-foreground font-bold uppercase">Total Balance</div>
              <div className="text-xl font-bold">R {totalBalance.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Search &amp; Filter</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by name, account #, ID or cell..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="loans-search" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-56" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : loans.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
                No loans yet. Upload a CSV or add a new one.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[90px]">Date</TableHead>
                    <TableHead>Account #</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>ID Number</TableHead>
                    <TableHead>Cell</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="min-w-[190px]">Status</TableHead>
                    <TableHead>System Reminder</TableHead>
                    <TableHead>Comments</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loans.map(l => (
                    <TableRow key={l.id} className={`${ROW_COLORS[l.status] || ''} border-b`} data-testid={`loan-row-${l.account_no || l.id}`}>
                      <TableCell className="font-mono text-xs">{l.date || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{l.account_no || '-'}</TableCell>
                      <TableCell className="font-medium">{l.first_name} {l.surname}</TableCell>
                      <TableCell className="font-mono text-xs">{l.id_number || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{l.cell_number || '-'}</TableCell>
                      <TableCell className="text-xs">{l.channel || '-'}</TableCell>
                      <TableCell className="text-right font-semibold">R {Number(l.balance || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</TableCell>
                      <TableCell>
                        <Select value={l.status} onValueChange={(v) => changeStatus(l, v)}>
                          <SelectTrigger className={`h-8 text-xs font-bold ${STATUS_CHIP[l.status] || ''}`} data-testid={`status-select-${l.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {l.system_reminder ? (
                          <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold border ${REMINDER_CHIP[l.system_reminder] || 'bg-slate-800 text-white border-slate-300'}`}>
                            {l.system_reminder}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-xs max-w-[180px] truncate" title={l.comments}>{l.comments || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(l)} data-testid={`edit-btn-${l.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteLoan(l)} data-testid={`delete-btn-${l.id}`}>
                          <Trash2 className="h-4 w-4 text-rose-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Edit Loan' : 'New Loan'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="form-date" /></div>
            <div><Label>Account Number</Label><Input value={form.account_no} onChange={(e) => setForm({ ...form, account_no: e.target.value })} data-testid="form-account" /></div>
            <div><Label>First Name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="form-fname" /></div>
            <div><Label>Surname</Label><Input value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} data-testid="form-sname" /></div>
            <div><Label>ID Number</Label><Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} data-testid="form-id" /></div>
            <div><Label>Cell Number</Label><Input value={form.cell_number} onChange={(e) => setForm({ ...form, cell_number: e.target.value })} data-testid="form-cell" /></div>
            <div><Label>Channel</Label><Input value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} /></div>
            <div><Label>Own Reference</Label><Input value={form.own_reference} onChange={(e) => setForm({ ...form, own_reference: e.target.value })} /></div>
            <div><Label>Balance (R)</Label><Input type="number" min="0" step="0.01" value={form.balance} onChange={(e) => setForm({ ...form, balance: parseFloat(e.target.value) || 0 })} /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Comments</Label><Textarea rows={2} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} /></div>
            <div className="col-span-2"><Label>Action</Label><Input value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={saveLoan} className="bg-blue-600 hover:bg-blue-700" data-testid="save-loan-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bulk paste dialog */}
      <Dialog open={showPaste} onOpenChange={setShowPaste}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardPaste className="h-5 w-5" /> Paste from Excel / Google Sheets
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-muted/50 p-3 text-xs space-y-1">
              <p className="font-semibold">Copy rows from your spreadsheet and paste them below. One application per line.</p>
              <p>
                Column order (tab-separated): <br/>
                <code className="font-mono">
                  Account No &nbsp; Surname &nbsp; First Name &nbsp; ID Number &nbsp; Cell &nbsp; Channel &nbsp; Own Reference &nbsp; Balance &nbsp; Status
                </code>
              </p>
              <p className="text-muted-foreground">
                Example: <br/>
                <code className="font-mono">
                  C0130713&nbsp;&nbsp;Fortuin&nbsp;&nbsp;Carl&nbsp;&nbsp;0406055187081&nbsp;&nbsp;0828835500&nbsp;&nbsp;ONLINE APPLICATION&nbsp;&nbsp;Own Ref&nbsp;&nbsp;0&nbsp;&nbsp;Pending
                </code>
              </p>
              <p className="text-muted-foreground">
                Optional: include a <b>date</b> (e.g. 2026-04-20 or 4/20/2026) as the very first column — otherwise the Default Date below is used.
              </p>
            </div>
            <div>
              <Label>Default Date (used when no date column is pasted)</Label>
              <Input type="date" value={pasteDate} onChange={(e) => setPasteDate(e.target.value)} data-testid="paste-default-date" />
            </div>
            <div>
              <Label>Rows</Label>
              <Textarea
                rows={10}
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={"C0130713\tFortuin\tCarl\t0406055187081\t0828835500\tONLINE APPLICATION\tOwn Ref\t0\tPending"}
                className="font-mono text-xs"
                data-testid="paste-textarea"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                {pasteText.split('\n').filter(l => l.trim()).length} row(s) detected.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaste(false)}>Cancel</Button>
            <Button onClick={handleBulkPaste} className="bg-blue-600 hover:bg-blue-700" data-testid="import-paste-btn">
              <ClipboardPaste className="h-4 w-4 mr-1.5" /> Import Rows
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Public API / Embed Snippet Dialog */}
      <Dialog open={showApi} onOpenChange={setShowApi}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Code2 className="h-5 w-5" /> Public API — 2K2Day Loan Tracker</DialogTitle>
          </DialogHeader>
          {!apiConfig ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1 bg-slate-50 border rounded-lg p-3 text-xs">
                <div><span className="text-slate-500">Endpoint:</span> <code className="font-mono">{apiConfig.endpoint}</code></div>
                <div>
                  <span className="text-slate-500">Header:</span>{' '}
                  <code className="font-mono">X-API-Key: {apiConfig.api_key}</code>
                  <Button size="sm" variant="ghost" className="ml-2 h-6 px-2" onClick={() => copyToClipboard(apiConfig.api_key, 'API key')} data-testid="copy-api-key">
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <div><span className="text-slate-500">Accepted fields:</span> <span className="font-mono">{apiConfig.accepted_fields.join(', ')}</span></div>
                {apiConfig.rate_limits && (
                  <div>
                    <span className="text-slate-500">Rate limits:</span>{' '}
                    <span className="font-mono" data-testid="rate-limits">
                      {apiConfig.rate_limits.per_minute_per_ip}/min per IP · {apiConfig.rate_limits.per_hour_per_ip}/hour per IP · {apiConfig.rate_limits.per_hour_global}/hour global
                    </span>
                  </div>
                )}
                <p className="text-[11px] text-amber-700 mt-1">Anyone with this key can submit loan applications. Every submission lands as <b>PENDING</b>. Rotate the key by setting <code>LOAN_APPLY_API_KEY</code> in backend env.</p>
              </div>

              {/* Snippet tabs */}
              <div className="flex gap-2 border-b" data-testid="api-tabs">
                {[['html', 'HTML Embed'], ['javascript', 'JavaScript'], ['curl', 'cURL']].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => setApiTab(k)}
                    className={`px-3 py-1.5 text-sm ${apiTab === k ? 'border-b-2 border-blue-600 text-blue-700 font-semibold' : 'text-slate-500 hover:text-slate-800'}`}
                    data-testid={`api-tab-${k}`}
                  >
                    {l}
                  </button>
                ))}
              </div>

              <div className="relative">
                <pre className="bg-slate-950 text-emerald-200 text-xs font-mono p-4 rounded-lg overflow-x-auto max-h-96" data-testid="api-snippet">
                  <code>{apiConfig[apiTab === 'html' ? 'html_embed' : apiTab]}</code>
                </pre>
                <Button
                  size="sm"
                  className="absolute top-2 right-2 bg-slate-800 hover:bg-slate-700 text-white"
                  onClick={() => copyToClipboard(apiConfig[apiTab === 'html' ? 'html_embed' : apiTab], `${apiTab} snippet`)}
                  data-testid="copy-api-snippet"
                >
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApi(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
